import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "app/store";
import Menu from "components/common/Menu";
import { SideBarTabs } from "constants/tabs";
import { useTabActive } from "hook/useTabActive";
import useAdminOnly from "hook/useAdminOnly";
import { CommonStyles } from "styles/CommonStyles";
import { GetSetting, UpdateAppUpdatePolicy } from "api/services/settings";

const DEFAULT_MINIMUM_BUILD = "21";
const DEFAULT_LATEST_BUILD = "21";
const DEFAULT_TITLE = "Update required";
const DEFAULT_MESSAGE =
  "Please update BossCar to continue using the app.";

type FeedbackType = {
  type: "success" | "error";
  text: string;
};

function AppUpdatePage() {
  const { user } = useSelector((state: RootState) => state.user);
  const [androidMinimumBuild, setAndroidMinimumBuild] = useState(
    DEFAULT_MINIMUM_BUILD
  );
  const [androidLatestBuild, setAndroidLatestBuild] =
    useState(DEFAULT_LATEST_BUILD);
  const [playstore, setPlaystore] = useState("");
  const [androidUpdateTitle, setAndroidUpdateTitle] = useState(DEFAULT_TITLE);
  const [androidUpdateMessage, setAndroidUpdateMessage] =
    useState(DEFAULT_MESSAGE);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<FeedbackType | null>(null);

  useAdminOnly();
  useTabActive(SideBarTabs.AppUpdate.name);

  useEffect(() => {
    const loadSetting = async () => {
      const response = await GetSetting({ setLoading });
      if (response.status !== 200 || !response.detail) {
        setFeedback({
          type: "error",
          text: response.message || "Could not load app update settings.",
        });
        return;
      }

      const detail = response.detail;
      setAndroidMinimumBuild(
        String(detail.androidMinimumBuild ?? DEFAULT_MINIMUM_BUILD)
      );
      setAndroidLatestBuild(
        String(detail.androidLatestBuild ?? DEFAULT_LATEST_BUILD)
      );
      setPlaystore(detail.playstore || "");
      setAndroidUpdateTitle(detail.androidUpdateTitle || DEFAULT_TITLE);
      setAndroidUpdateMessage(detail.androidUpdateMessage || DEFAULT_MESSAGE);
    };

    loadSetting();
  }, []);

  const parseBuild = (value: string, label: string): number => {
    const parsed = Number(value);
    if (!Number.isInteger(parsed) || parsed < 0) {
      throw new Error(`${label} must be a non-negative whole number.`);
    }
    return parsed;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFeedback(null);

    try {
      const minimumBuild = parseBuild(
        androidMinimumBuild,
        "Minimum Android build"
      );
      const latestBuild = parseBuild(androidLatestBuild, "Latest Android build");
      const trimmedPlaystore = playstore.trim();
      const trimmedTitle = androidUpdateTitle.trim();
      const trimmedMessage = androidUpdateMessage.trim();

      if (!trimmedPlaystore) {
        throw new Error("Play Store URL is required.");
      }
      if (!trimmedTitle || !trimmedMessage) {
        throw new Error("Update title and message are required.");
      }
      if (latestBuild < minimumBuild) {
        throw new Error("Latest build cannot be lower than minimum build.");
      }

      const response = await UpdateAppUpdatePolicy({
        setLoading: setSaving,
        androidMinimumBuild: minimumBuild,
        androidLatestBuild: latestBuild,
        androidUpdateTitle: trimmedTitle,
        androidUpdateMessage: trimmedMessage,
        playstore: trimmedPlaystore,
      });

      if (response.status !== 200) {
        throw new Error(response.message || "Could not save update policy.");
      }

      setFeedback({
        type: "success",
        text: "App update policy saved.",
      });
    } catch (error: any) {
      setSaving(false);
      setFeedback({
        type: "error",
        text: error?.message || "Could not save update policy.",
      });
    }
  };

  if (user?.role !== "Admin") {
    return (
      <div className={CommonStyles.pageWrapper}>
        <Menu />
        <div className={ClassName.container}>
          <div className={ClassName.card}>Admin access is required.</div>
        </div>
      </div>
    );
  }

  return (
    <div className={CommonStyles.pageWrapper}>
      <Menu />
      <div className={ClassName.container}>
        <div className={ClassName.header}>
          <div className={ClassName.headerText}>
            <span className={ClassName.title}>App Update</span>
            <span className={ClassName.subtitle}>
              Force old Android builds to update from Google Play.
            </span>
          </div>
          <div className={ClassName.buildPill}>
            Latest build: {androidLatestBuild || "Not set"}
          </div>
        </div>

        <form className={ClassName.card} onSubmit={handleSubmit}>
          <div className={ClassName.grid}>
            <div className={ClassName.fieldGroup}>
              <label className={ClassName.label}>Minimum Android build</label>
              <input
                type="number"
                min="0"
                className={ClassName.input}
                value={androidMinimumBuild}
                onChange={(event) =>
                  setAndroidMinimumBuild(event.target.value)
                }
              />
              <span className={ClassName.helpText}>
                Builds below this number are blocked.
              </span>
            </div>

            <div className={ClassName.fieldGroup}>
              <label className={ClassName.label}>Latest Android build</label>
              <input
                type="number"
                min="0"
                className={ClassName.input}
                value={androidLatestBuild}
                onChange={(event) => setAndroidLatestBuild(event.target.value)}
              />
              <span className={ClassName.helpText}>
                Informational value for the newest release.
              </span>
            </div>
          </div>

          <div className={ClassName.fieldGroup}>
            <label className={ClassName.label}>Play Store URL</label>
            <input
              type="url"
              className={ClassName.input}
              value={playstore}
              onChange={(event) => setPlaystore(event.target.value)}
              placeholder="https://play.google.com/store/apps/details?id=com.bosscarnetwork.app"
            />
          </div>

          <div className={ClassName.fieldGroup}>
            <label className={ClassName.label}>Update title</label>
            <input
              type="text"
              className={ClassName.input}
              value={androidUpdateTitle}
              onChange={(event) => setAndroidUpdateTitle(event.target.value)}
              maxLength={80}
            />
          </div>

          <div className={ClassName.fieldGroup}>
            <label className={ClassName.label}>Update message</label>
            <textarea
              className={ClassName.textarea}
              value={androidUpdateMessage}
              onChange={(event) => setAndroidUpdateMessage(event.target.value)}
              rows={4}
              maxLength={240}
            />
          </div>

          {feedback && (
            <div
              className={`${ClassName.feedback} ${
                feedback.type === "success"
                  ? ClassName.feedbackSuccess
                  : ClassName.feedbackError
              }`}
            >
              {feedback.text}
            </div>
          )}

          <div className={ClassName.footer}>
            <div className={ClassName.note}>
              If the app cannot reach the server, users are allowed in so they
              are not locked out by a temporary network issue.
            </div>
            <button
              type="submit"
              className={ClassName.saveButton}
              disabled={loading || saving}
            >
              {saving ? "Saving..." : "Save policy"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AppUpdatePage;

const ClassName = {
  container:
    "flex flex-1 flex-col gap-4 text-primaryText",
  header:
    "flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-white shadow-primaryShadow rounded-lg p-5",
  headerText: "flex flex-col",
  title: "text-2xl font-bold",
  subtitle: "text-sm text-secondaryText mt-1",
  buildPill:
    "w-fit rounded-full bg-bgSecondary px-4 py-2 text-xs font-semibold text-secondaryText",
  card:
    "flex flex-col gap-5 bg-white shadow-primaryShadow rounded-lg p-5 max-w-4xl",
  grid: "grid grid-cols-1 md:grid-cols-2 gap-4",
  fieldGroup: "flex flex-col gap-2",
  label: "text-sm font-semibold text-primaryText",
  input:
    "w-full h-11 rounded-md border border-bgSecondary px-3 text-sm text-primaryText outline-none focus:border-primary",
  textarea:
    "w-full rounded-md border border-bgSecondary px-3 py-2 text-sm text-primaryText outline-none focus:border-primary resize-none",
  helpText: "text-xs text-secondaryText",
  feedback: "rounded-md px-3 py-2 text-sm font-medium border",
  feedbackSuccess: "border-green-200 bg-green-50 text-green-700",
  feedbackError: "border-red-200 bg-red-50 text-red-700",
  footer:
    "flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2",
  note: "text-xs text-secondaryText max-w-xl leading-5",
  saveButton:
    "h-11 px-5 rounded-md bg-primary text-white text-sm font-semibold disabled:opacity-60",
};
